// AM dashboard, "Accounts at a glance". PURE: one row per account, worst first.

import type {
  AdChannel,
  HomeAccount,
  HomeAccountHealth,
  SpineDay,
} from '../../types/advertisingHome.js';
import type { AmContext } from '../../types/amDashboard.js';
import {
  BrandAverages,
  classifyCpl,
  compareVerdictsWorstFirst,
  type CplVerdict,
} from '../advertisingHome/noLeads.js';
import { addDaysYmd, sumWindow } from '../advertisingHome/spine.js';
import type { PaceStatus } from './pace.js';

export interface GlanceRow {
  accountId: string;
  name: string;
  health: HomeAccountHealth;
  /** "OK" or a short problem list ("Meta not connected", "Google feed failing"). */
  connection: { ok: boolean; text: string };
  metaPace: { pace: number | null; status: PaceStatus | null } | null;
  googlePace: { pace: number | null; status: PaceStatus | null } | null;
  currency: string;
  spend7: number;
  leads7: number;
  leadsPrev7: number;
  /** The one rule's verdict for the blended 7 days (never a $0 CPL). */
  verdict: CplVerdict;
  openAlerts: number;
}

const HEALTH_RANK: Record<HomeAccountHealth, number> = { critical: 0, attention: 1, ontrack: 2 };

export function connectionOf(c: AmContext['accounts'][number] | undefined): { ok: boolean; text: string } {
  if (!c) return { ok: true, text: 'OK' };
  const problems: string[] = [];
  if (c.runsMeta && c.metaConnected === false) problems.push('Meta not connected');
  if (c.runsGoogle && c.googleConnected === false) problems.push('Google not connected');
  for (const p of c.failingConnections) problems.push(`${p} failing`);
  return problems.length > 0 ? { ok: false, text: problems.join(', ') } : { ok: true, text: 'OK' };
}

export function buildGlance(input: {
  accounts: readonly HomeAccount[];
  rows: readonly SpineDay[];
  /** The last complete loaded day (the older of the two channels'). */
  lastDay: string;
  healthByAccount: ReadonlyMap<string, HomeAccountHealth>;
  openAlertsByAccount: ReadonlyMap<string, number>;
  connections: ReadonlyMap<string, AmContext['accounts'][number]>;
  paceByAccount: ReadonlyMap<string, { meta: GlanceRow['metaPace']; google: GlanceRow['googlePace'] }>;
  brandAverages: BrandAverages;
}): GlanceRow[] {
  const byAccount = new Map<string, SpineDay[]>();
  for (const r of input.rows) {
    const list = byAccount.get(r.accountId);
    if (list) list.push(r);
    else byAccount.set(r.accountId, [r]);
  }
  const from7 = addDaysYmd(input.lastDay, -6);
  const prevTo = addDaysYmd(from7, -1);
  const prevFrom = addDaysYmd(prevTo, -6);
  const out = input.accounts.map((a): GlanceRow => {
    const days = byAccount.get(a.id) ?? [];
    const w = sumWindow(days, from7, input.lastDay);
    const prev = sumWindow(days, prevFrom, prevTo);
    const currency = w.currency ?? a.currency;
    const pick = (t: typeof w, k: 'spendByCurrency' | 'leadsByCurrency'): number =>
      t[k].find((x) => x.currency === currency)?.amount ?? 0;
    const spend = pick(w, 'spendByCurrency');
    const leads = pick(w, 'leadsByCurrency');
    // The blended verdict: leads exist => a plain CPL; none => the worse channel's
    // own no-leads judgement against ITS brand average (the one rule).
    let verdict: CplVerdict = classifyCpl({ spend, leads, brandCpl: null });
    if (leads <= 0 && spend > 0) {
      const perChannel = (['meta', 'google'] as AdChannel[]).map((ch) => {
        const cw = sumWindow(days.filter((d) => d.channel === ch), from7, input.lastDay);
        return classifyCpl({
          spend: cw.spendByCurrency.find((x) => x.currency === currency)?.amount ?? 0,
          leads: 0,
          brandCpl: input.brandAverages.cpl(a.brandAccountId, ch, currency),
        });
      });
      verdict = perChannel.sort(compareVerdictsWorstFirst)[0] ?? verdict;
    }
    const pace = input.paceByAccount.get(a.id);
    return {
      accountId: a.id,
      name: a.name,
      health: input.healthByAccount.get(a.id) ?? 'ontrack',
      connection: connectionOf(input.connections.get(a.id)),
      metaPace: pace?.meta ?? null,
      googlePace: pace?.google ?? null,
      currency,
      spend7: spend,
      leads7: leads,
      leadsPrev7: pick(prev, 'leadsByCurrency'),
      verdict,
      openAlerts: input.openAlertsByAccount.get(a.id) ?? 0,
    };
  });
  return out.sort(
    (x, y) =>
      HEALTH_RANK[x.health] - HEALTH_RANK[y.health] ||
      Number(x.connection.ok) - Number(y.connection.ok) ||
      compareVerdictsWorstFirst(x.verdict, y.verdict) ||
      y.openAlerts - x.openAlerts ||
      x.name.localeCompare(y.name),
  );
}
